import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { facilitiesAPI, usersAPI } from '../services/api';
import { PASSWORD_MIN_LENGTH } from '../constants/authPolicy';
import { Badge, EmptyState, InfoBanner } from '../components/ui';
import { 
  UserPlus, 
  Edit, 
  Trash2, 
  Save, 
  X, 
  Eye, 
  EyeOff,
  Shield,
  Users as UsersIcon,
  User as UserIcon,
  Building2
} from 'lucide-react';

const Users = () => {
  const { getAllUsers, getAllRoles, addUser, updateUser, deleteUser, user: currentUser, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showPasswords, setShowPasswords] = useState({});
  const [allFacilities, setAllFacilities] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [facilityFilter, setFacilityFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const filteredUsers = React.useMemo(() => {
    return users.filter(u => {
      const q = userSearch.toLowerCase();
      if (q && !(u.name || '').toLowerCase().includes(q) && !(u.username || '').toLowerCase().includes(q) && !(u.email || '').toLowerCase().includes(q)) return false;
      if (facilityFilter && !(u.facilities || []).some(f => String(f.facility_id) === facilityFilter)) return false;
      if (statusFilter === 'active' && u.isActive === false) return false;
      if (statusFilter === 'inactive' && u.isActive !== false) return false;
      return true;
    });
  }, [users, userSearch, facilityFilter, statusFilter]);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    name: '',
    email: '',
    phone: '',
    roleId: null,
    isActive: true,
    facilityIds: []
  });

  useEffect(() => {
    loadUsers();
    loadRoles();
    loadAllFacilities();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      const allRoles = await getAllRoles();
      setRoles(Array.isArray(allRoles) ? allRoles : []);
    } catch (error) {
      console.error('Error loading roles:', error);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAllFacilities = async () => {
    try {
      const res = await facilitiesAPI.get();
      if (res.success && Array.isArray(res.data)) {
        setAllFacilities(res.data);
      }
    } catch (error) {
      console.error('Error loading facilities:', error);
    }
  };

  const loadUsers = async () => {
    try {
      setLoading(true);
      const allUsers = await getAllUsers();
      setUsers(Array.isArray(allUsers) ? allUsers : []);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users: ' + error.message);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = currentUser?.role_name === 'Admin';

  const resetForm = () => {
    // Super Admin: default to Admin; Admin: default to Manager; fallback to first role
    let defaultRole = roles.find(r => r.name === 'Admin') || roles[0];
    if (isAdmin) {
      defaultRole = roles.find(r => r.name === 'Manager') || defaultRole;
    }
    setFormData({
      username: '',
      password: '',
      name: '',
      email: '',
      phone: '',
      roleId: defaultRole?.id || null,
      isActive: true,
      facilityIds: []
    });
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    setError('');
  };

  const getRoleNameById = (roleId) => {
    const selectedRole = roles.find((role) => role.id === Number(roleId));
    return selectedRole?.name || '';
  };

  const isSelectedFacilityAdmin = () => getRoleNameById(formData.roleId).toLowerCase() === 'admin';

  const validateForm = () => {
    if (!formData.username.trim()) {
      setError('Username is required');
      return false;
    }
    if (!editingId && !formData.password.trim()) {
      setError('Password is required for new users');
      return false;
    }
    if (formData.password && formData.password.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters`);
      return false;
    }
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (formData.phone && formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Phone must be a 10-digit number');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.roleId) {
      setError('A role must be assigned to the user');
      return false;
    }
    if (isSelectedFacilityAdmin() && formData.facilityIds.length !== 1) {
      setError('Admin must be assigned exactly one facility');
      return false;
    }
    return true;
  };

  const handleAdd = async () => {
    if (!validateForm()) return;

    if (Array.isArray(users) && users.some(u => u.username.toLowerCase() === formData.username.toLowerCase().trim())) {
      setError('Username already exists');
      return;
    }

    try {
      const newUser = await addUser(formData);
      // Assign facilities if selected
      if (formData.facilityIds.length > 0 && newUser?.id) {
        await usersAPI.setFacilities(newUser.id, formData.facilityIds, formData.facilityIds[0]);
      }
      await loadUsers();
      resetForm();
      setShowAddForm(false);
      setSuccess('User added successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to add user: ' + error.message);
    }
  };

  const handleEdit = (user) => {
    setEditingId(user.id);
    let roleId = user.roleId || user.role_id || null;
    if (!roleId && user.role_name) {
      const role = roles.find(r => r.name === user.role_name);
      roleId = role?.id || null;
    }

    const userFacilityIds = (user.facilities || []).map(f => f.facility_id);
    
    setFormData({
      username: user.username,
      password: '',
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      roleId: roleId || null,
      isActive: user.is_active !== false && user.isActive !== false,
      facilityIds: userFacilityIds
    });
    setError('');
    setSuccess('');
  };

  const handleUpdate = async () => {
    if (!validateForm()) return;

    const usernameExists = Array.isArray(users) && users.some(
      u => u.id !== editingId && 
           u.username.toLowerCase() === formData.username.toLowerCase().trim()
    );
    if (usernameExists) {
      setError('Username already exists');
      return;
    }

    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      delete updateData.facilityIds;
      await updateUser(editingId, updateData);
      // Update facility assignments
      await usersAPI.setFacilities(
        editingId,
        formData.facilityIds,
        formData.facilityIds[0] || null
      );
      await loadUsers();
      resetForm();
      setEditingId(null);
      setSuccess('User updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to update user: ' + error.message);
    }
  };

  const handleDelete = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This will deactivate the account.`)) {
      return;
    }
    try {
      const result = await deleteUser(userId);
      if (result && result.success) {
        loadUsers();
        setSuccess('User deleted successfully');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError((result && result.error) || 'Failed to delete user');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete user');
    }
  };

  const cancelEdit = () => {
    resetForm();
    setEditingId(null);
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-ga-text-primary mb-2">User Management</h1>
            <p className="text-ga-text-muted">Manage system users and their permissions</p>
          </div>
          {(isSuperAdmin || isAdmin) && (
            <button
              onClick={() => {
                resetForm();
                setShowAddForm(true);
                setEditingId(null);
              }}
              className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
            >
              <UserPlus className="h-5 w-5" />
              <span>Add User</span>
            </button>
          )}
        </div>

        {/* Messages */}
        {error && (
          <InfoBanner variant="danger" className="mb-4">{error}</InfoBanner>
        )}

        {success && (
          <InfoBanner variant="success" className="mb-4">{success}</InfoBanner>
        )}

        {/* Add User Form */}
        {showAddForm && (
          <div className="mb-6 p-6 ga-card rounded-ga-md shadow-ga-soft border border-ga-border-subtle">
            <h2 className="text-xl font-semibold text-ga-text-primary mb-4">Add New User</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Username *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                  placeholder="Enter username"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Password *</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                  placeholder={`Enter password (min ${PASSWORD_MIN_LENGTH} characters)`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Phone (for OTP login)</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                  placeholder="10-digit phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Role</label>
                <select
                  name="roleId"
                  value={formData.roleId || ''}
                  onChange={(e) => {
                    const nextRoleId = parseInt(e.target.value, 10) || null;
                    const nextRoleName = getRoleNameById(nextRoleId).toLowerCase();
                    setFormData((prev) => ({
                      ...prev,
                      roleId: nextRoleId,
                      facilityIds: nextRoleName === 'admin' && prev.facilityIds.length > 1
                        ? [prev.facilityIds[0]]
                        : prev.facilityIds
                    }));
                  }}
                  className="ga-input w-full rounded-ga-sm"
                >
                  <option value="">Select a role</option>
                  {roles
                    .filter(role => {
                      if (isSuperAdmin) return true;
                      // Facility Admins can only create Manager/Staff users
                      const name = (role.name || '').toLowerCase();
                      return name === 'manager' || name === 'staff';
                    })
                    .map(role => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 rounded border-ga-border-subtle accent-ga-primary"
                />
                <label className="ml-2 block text-sm text-ga-text-primary">Active</label>
              </div>
            </div>
            {/* Facility Assignments */}
            {allFacilities.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  <Building2 className="h-4 w-4 inline mr-1" />
                  Facility Access
                </label>
                {isSelectedFacilityAdmin() && (
                  <p className="text-xs text-ga-text-muted mb-2">
                    Admin users are limited to exactly one facility.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {allFacilities.map((fac) => (
                    <label key={fac.id} className="flex items-center space-x-2 p-2 border border-ga-border-subtle rounded-ga-sm hover:bg-ga-bg-surface-subtle cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.facilityIds.includes(fac.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData((prev) => {
                            if (isSelectedFacilityAdmin()) {
                              return {
                                ...prev,
                                facilityIds: checked ? [fac.id] : []
                              };
                            }
                            return {
                              ...prev,
                              facilityIds: checked
                                ? [...prev.facilityIds, fac.id]
                                : prev.facilityIds.filter(id => id !== fac.id)
                            };
                          });
                        }}
                        className="h-4 w-4 rounded border-ga-border-subtle accent-ga-primary"
                      />
                      <span className="text-sm text-ga-text-primary">{fac.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 flex space-x-2">
              <button
                onClick={handleAdd}
                className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
              >
                <Save className="h-4 w-4" />
                <span>Save User</span>
              </button>
              <button
                onClick={() => {
                  setShowAddForm(false);
                  resetForm();
                }}
                className="ga-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        )}

        {/* Search & Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by name, username or email…"
            value={userSearch}
            onChange={e => setUserSearch(e.target.value)}
            className="flex-1 px-4 py-2 rounded-lg border border-ga-border-subtle text-sm focus:outline-none focus:ring-2 focus:ring-ga-primary/30"
          />
          <select
            value={facilityFilter}
            onChange={e => setFacilityFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-ga-border-subtle text-sm focus:outline-none"
          >
            <option value="">All facilities</option>
            {allFacilities.map(f => (
              <option key={f.id} value={String(f.id)}>{f.name}</option>
            ))}
          </select>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="px-3 py-2 rounded-lg border border-ga-border-subtle text-sm focus:outline-none"
          >
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        {/* Users Table */}
        <div className="ga-card rounded-ga-md shadow-ga-soft overflow-hidden mt-4">
          <div className="overflow-x-auto">
            <table className="ga-table min-w-full divide-y divide-ga-border-subtle">
              <thead className="bg-ga-bg-surface-subtle">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">
                    Facilities
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ga-text-muted uppercase tracking-wider">
                    Created
                  </th>
                  {isSuperAdmin && (
                    <th className="px-6 py-3 text-right text-xs font-medium text-ga-text-muted uppercase tracking-wider">
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-ga-border-subtle">
                {!Array.isArray(filteredUsers) || filteredUsers.length === 0 ? (
                  <tr>
                    <td colSpan={isSuperAdmin ? 7 : 6} className="px-6 py-8">
                      {loading ? (
                        <p className="text-center text-ga-text-muted">Loading users...</p>
                      ) : (
                        <EmptyState
                          icon={UsersIcon}
                          title="No users found"
                          description="Try adjusting your search or filters, or add a new user to get started."
                        />
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-ga-bg-surface-subtle">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-ga-primary-soft flex items-center justify-center">
                            {user.role === 'super_admin' ? (
                              <Shield className="h-5 w-5 text-ga-primary" />
                            ) : (
                              <UserIcon className="h-5 w-5 text-ga-primary" />
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-ga-text-primary">{user.name || user.username}</div>
                            <div className="text-sm text-ga-text-muted">@{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {(() => {
                          const userRole = roles.find(r => r.id === user.roleId) ||
                                         (user.role_name ? roles.find(r => r.name === user.role_name) : null);
                          const roleName = userRole?.name || user.role_name || 'No Role';
                          const isSuperAdminRole = roleName === 'Super Admin';
                          return (
                            <Badge variant={isSuperAdminRole ? 'info' : 'neutral'}>
                              {roleName}
                            </Badge>
                          );
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-ga-text-muted">
                        {user.email || '—'}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {(user.facilities || []).length > 0 ? (
                            user.facilities.map(f => (
                              <Badge key={f.facility_id} variant="neutral">
                                {f.facility_name}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-xs text-ga-text-subtle">None</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Badge variant={user.isActive !== false ? 'success' : 'danger'}>
                          {user.isActive !== false ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-ga-text-muted">
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString()
                          : '—'}
                      </td>
                      {isSuperAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            {editingId === user.id ? (
                              <>
                              <button
                                  onClick={handleUpdate}
                                  className="text-ga-success hover:opacity-80"
                                  title="Save"
                                >
                                  <Save className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={cancelEdit}
                                  className="text-ga-text-muted hover:text-ga-text-primary"
                                  title="Cancel"
                                >
                                  <X className="h-5 w-5" />
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handleEdit(user)}
                                  className="text-ga-info hover:opacity-80"
                                  title="Edit"
                                  disabled={user.id === 1 && !isSuperAdmin}
                                >
                                  <Edit className="h-5 w-5" />
                                </button>
                                {user.id !== currentUser?.id && (
                                  <button
                                    onClick={() => handleDelete(user.id, user.username, user.role_name)}
                                    className="text-ga-danger hover:opacity-80"
                                    title="Delete (Super Admin can delete any user including Admins)"
                                  >
                                    <Trash2 className="h-5 w-5" />
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Form (Inline) */}
        {editingId && !showAddForm && (
          <div className="mt-6 p-6 ga-card rounded-ga-md shadow-ga-soft border border-ga-border-subtle">
            <h2 className="text-xl font-semibold text-ga-text-primary mb-4">Edit User</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Username *</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">
                  New Password {editingId && '(leave blank to keep current)'}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                  placeholder={`Enter new password (min ${PASSWORD_MIN_LENGTH} characters)`}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Phone (for OTP login)</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="ga-input w-full rounded-ga-sm"
                  placeholder="10-digit phone number"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ga-text-muted mb-1">Role</label>
                <select
                  name="roleId"
                  value={formData.roleId || ''}
                  onChange={(e) => {
                    const nextRoleId = parseInt(e.target.value, 10) || null;
                    const nextRoleName = getRoleNameById(nextRoleId).toLowerCase();
                    setFormData((prev) => ({
                      ...prev,
                      roleId: nextRoleId,
                      facilityIds: nextRoleName === 'admin' && prev.facilityIds.length > 1
                        ? [prev.facilityIds[0]]
                        : prev.facilityIds
                    }));
                  }}
                  className="ga-input w-full rounded-ga-sm"
                  disabled={editingId === 1} // Cannot change super admin role
                >
                  <option value="">Select a role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleInputChange}
                  className="h-4 w-4 rounded border-ga-border-subtle accent-ga-primary"
                  disabled={editingId === 1}
                />
                <label className="ml-2 block text-sm text-ga-text-primary">Active</label>
              </div>
            </div>
            {/* Facility Assignments */}
            {allFacilities.length > 0 && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-ga-text-muted mb-2">
                  <Building2 className="h-4 w-4 inline mr-1" />
                  Facility Access
                </label>
                {isSelectedFacilityAdmin() && (
                  <p className="text-xs text-ga-text-muted mb-2">
                    Admin users are limited to exactly one facility.
                  </p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {allFacilities.map((fac) => (
                    <label key={fac.id} className="flex items-center space-x-2 p-2 border border-ga-border-subtle rounded-ga-sm hover:bg-ga-bg-surface-subtle cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.facilityIds.includes(fac.id)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData((prev) => {
                            if (isSelectedFacilityAdmin()) {
                              return {
                                ...prev,
                                facilityIds: checked ? [fac.id] : []
                              };
                            }
                            return {
                              ...prev,
                              facilityIds: checked
                                ? [...prev.facilityIds, fac.id]
                                : prev.facilityIds.filter(id => id !== fac.id)
                            };
                          });
                        }}
                        className="h-4 w-4 rounded border-ga-border-subtle accent-ga-primary"
                      />
                      <span className="text-sm text-ga-text-primary">{fac.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-4 flex space-x-2">
                        <button
                          onClick={handleUpdate}
                          className="ga-btn-primary inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
                        >
                <Save className="h-4 w-4" />
                <span>Save Changes</span>
              </button>
              <button
                onClick={cancelEdit}
                className="ga-btn-ghost inline-flex items-center gap-2 px-4 py-2 rounded-ga-md"
              >
                <X className="h-4 w-4" />
                <span>Cancel</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Users;

